import * as angular from 'angular';
import * as _ from 'lodash';

import { ConnectionPoint, Dictionary, DigitalSet, Identifier, IFrequency } from '../../models';
import { ActualDataType, IdentifierType } from '../../models/enum';
import { IValueViewModel, IIdentifierVm } from '../../models/viewModels';
import { guidEmpty } from '../../common/constants';
import { IGroupService } from '../data';
import { ILoggerService } from '../general';

export interface IIdentifierStateService {
    getSelectedItem(): Identifier;
    canSelectItem(value?: Identifier): boolean;
    issueSelectionWarning(): void;
    selectedItem(value?: Identifier): Identifier;
    identifiers(value?: Identifier[]): Identifier[];
    formController: angular.IFormController;
    mergeViewModels(
        identifiers: Identifier[],
        viewModels: IValueViewModel[],
        connectionPoints?: Dictionary<ConnectionPoint>,
        digitalSets?: Dictionary<DigitalSet>
    ): IIdentifierVm[];
}

export class IdentifierStateService implements IIdentifierStateService {
    static serviceId = 'identifierStateService';

    private _selectedItem: Identifier;
    private _identifiers: Identifier[];

    formController: angular.IFormController;

    static $inject = ['$translate', 'loggerService'];

    constructor(
        private _$translate: angular.translate.ITranslateService,
        private _loggerService: ILoggerService
    ) {
    }

    identifiers(value?: Identifier[]): Identifier[] {
        if (arguments.length) {
            this._identifiers = value;
        }
        return this._identifiers;

    }

    getSelectedItem(): Identifier {
        return this._selectedItem;
    }

    selectedItem(value?: Identifier): Identifier {
        // prevent double selection
        if (arguments.length && value !== this._selectedItem) {
            if (this.canSelectItem()) {
                this._selectedItem = value;
            } else {
                this.issueSelectionWarning();
            }
        }

        return this._selectedItem;
    }

    issueSelectionWarning(): void {
        const msg = this._$translate.instant('Save or cancel changes before selecting another identifier.');
        this._loggerService.logWarning(msg);
    }

    canSelectItem(): boolean {
        return ((this.formController && this.formController.$pristine) || (!this.formController));
    }

    mergeViewModels(
        identifiers: Identifier[],
        viewModels: IValueViewModel[],
        connectionPoints: Dictionary<ConnectionPoint> = {},
        digitalSets: Dictionary<DigitalSet> = {}

    ): IIdentifierVm[] {
        let result = [];
        connectionPoints = connectionPoints || {};
        digitalSets = digitalSets || {};
        if (identifiers) {
            const dic: { [guid: string]: IValueViewModel } = {};
            viewModels.forEach(vm => dic[vm.guid] = vm);
            result = identifiers.map(i => {
                const vm = dic[i.guid];
                let ret = this.mergeCommon(i, vm);

                switch (i.type) {
                    case IdentifierType.Tag:
                        ret = this.getTagVm(ret, vm);
                        break;
                    case IdentifierType.CalculatedValue:
                        ret = this.getCalculatedVm(ret, vm);
                        break;
                    case IdentifierType.Linked:
                        ret = this.getLinkedVm(ret, vm);
                        break;
                    case IdentifierType.Validated:
                        ret = this.getValidatedVm(ret, vm);
                        break;
                    case IdentifierType.Target:
                        ret = this.getTargetVm(ret, vm);
                        break;
                    default:
                        this._loggerService.logError(`Cannot convert viewmodel for identifier type ${i.type}`);
                }

                if (ret.linkedGuid !== guidEmpty) {
                    ret.linked = dic[i.linkedGuid];
                }

                if (ret.tagDefinitionGuid !== guidEmpty) {
                    ret.tag = dic[i.tagDefinitionGuid];
                }

                const cp = connectionPoints[ret.acquisitionPointGuid];
                let connectionPointName = '';
                if (cp && cp.name) {
                    connectionPointName = cp.name;
                }
                ret['source'] = connectionPointName;
                ret.selectedVariableTypeGuid = i.variableTypeGuid;
                const foundIdentifier = _.find(identifiers, id => id.guid === i.guid);
                if (foundIdentifier) {
                    i.valueType = foundIdentifier.valueType;
                }
                if (i.dataType === ActualDataType.Digital) {
                    ret.variableTypeName = digitalSets[ret.digitalSetGuid]?.name;
                }
                return ret;
            });
        }
        result = _.sortBy(result, ['sortingOrder', 'name']);

        return result;
    }

    private mergeCommon(i: Identifier, vm: IValueViewModel): IIdentifierVm {
        const mapped = Object.assign({}, vm || {}) as IIdentifierVm;
        if (vm) {
            mapped.type = vm.identifierType;
            mapped.name = vm.shortName;
            mapped.tagDefinitionGuid = i.tagDefinitionGuid;
            mapped.linkedGuid = i.linkedGuid;
            mapped.sortingOrder = i.sortingOrder;
            mapped.tagName = i.tagDefinitionGuid === guidEmpty ? '' : vm.tagName;
            mapped.shortComment = i.shortComment; // prevent overwrite of shortcomment from VVM
            mapped.customProperties = i.customProperties;
            mapped.correspondanceTableGuid = i.correspondanceTableGuid;
            mapped.precedentCorrespondanceIdentifierGuid = i.precedentCorrespondanceIdentifierGuid;
            const aggregationPeriod: IFrequency = i.aggregationPeriod
                ? Object.assign({}, i.aggregationPeriod)
                : { interval: 0, intervalTimeUnit: 0 };
            mapped.aggregationPeriod = aggregationPeriod;
            mapped.isRolling = i.isRolling;
        }
        return mapped;
    }

    private getTagVm(t: IIdentifierVm, vm: IValueViewModel): IIdentifierVm {
        return t;
    }

    private getCalculatedVm(c: IIdentifierVm, vm: IValueViewModel): IIdentifierVm {
        c.calculatedValueUrl = `calculated.general({identifierGuid: '${c.guid}'})`;
        return c;
    }

    private getValidatedVm(v: IIdentifierVm, vm: IValueViewModel): IIdentifierVm {
        v.validatedValueUrl = `validated.general({identifierGuid: '${v.guid}'})`;
        return v;
    }

    private getLinkedVm(l: IIdentifierVm, vm: IValueViewModel): IIdentifierVm {
        l.linkedValueUrl = `linked({linkedGuid: '${l.linkedGuid}'})`;
        return l;
    }

    private getTargetVm(t: IIdentifierVm, vm: IValueViewModel): IIdentifierVm {
        t.targetValueUrl = `target.general({identifierGuid: '${t.guid}'})`;
        return t;
    }
}